import { useEffect, useState } from 'react';
import { Dialog, Portal, CloseButton, Button, Field, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AccountService from '@/services/AccountService';
import { toaster } from '@/components/ui/toaster';
import PasswordInput from '@/components/ui/foundation/PasswordInput';

const DisableTwoFactorDialog = ({ isOpen, method: twoFactorMethod, onClose, onDisabled }) => {
    const { t } = useTranslation('account');
    const [method, setMethod] = useState('password');
    const [value, setValue] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // E-mail 2FA is disabled with the password only.
    const allowCode = twoFactorMethod !== 'Email';

    useEffect(() => {
        if (!isOpen) return;
        setMethod('password');
        setValue('');
        setError('');
    }, [isOpen]);

    const handleDisable = async () => {
        setError('');
        setIsSubmitting(true);
        try {
            await AccountService.disableTwoFactor(method === 'password' ? { password: value } : { code: value });
            toaster.create({ type: 'success', title: t('settingsPage.twoFactor.toastDisabled') });
            onDisabled();
            onClose();
        } catch (err) {
            setError(err?.response?.status === 400
                ? t('settingsPage.twoFactor.errorDisableInvalid')
                : t('settingsPage.twoFactor.toastError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content bg="bg.card" color="white" border="1px solid" borderColor="border.primary" w={{ base: 'calc(100vw - 2rem)', md: 'md' }} maxW="100%">
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight="bold" fontSize="lg" mb={2}>{t('settingsPage.twoFactor.disableTitle')}</Text>
                            <Text color="text.grey" fontSize="sm" mb={4}>
                                {allowCode ? t('settingsPage.twoFactor.disableIntro') : t('settingsPage.twoFactor.disableIntroEmail')}
                            </Text>

                            <VStack gap={4} align="stretch">
                                {allowCode && (
                                    <HStack gap={2}>
                                        <Button
                                            size="sm"
                                            variant={method === 'password' ? 'primary' : 'outlined'}
                                            onClick={() => { setMethod('password'); setValue(''); }}
                                            type="button"
                                        >
                                            {t('settingsPage.twoFactor.methodPassword')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={method === 'code' ? 'primary' : 'outlined'}
                                            onClick={() => { setMethod('code'); setValue(''); }}
                                            type="button"
                                        >
                                            {t('settingsPage.twoFactor.methodCode')}
                                        </Button>
                                    </HStack>
                                )}

                                <Field.Root required>
                                    <Field.Label color="white">
                                        {method === 'password' ? t('settingsPage.twoFactor.passwordLabel') : t('settingsPage.twoFactor.codeLabel')}
                                        <Field.RequiredIndicator />
                                    </Field.Label>
                                    {method === 'password' ? (
                                        <PasswordInput value={value} onChange={(e) => setValue(e.target.value)} />
                                    ) : (
                                        <Input
                                            variant="primary"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)}
                                        />
                                    )}
                                </Field.Root>
                            </VStack>

                            {error && <Text color="red.400" fontSize="sm" mt={2}>{error}</Text>}

                            <Button variant="primary" mt={4} w="full" onClick={handleDisable} loading={isSubmitting} disabled={!value}>
                                {t('settingsPage.twoFactor.disableButton')}
                            </Button>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default DisableTwoFactorDialog;
